/**
 * サロンボード互換機能のための型定義
 * 既存のCustomer型、Reservation型を拡張・補完する形で設計
 */

/**
 * 予約番号フォーマット
 * BD: Beauty系予約
 * YE: 電話予約
 * WB: Web予約
 * OT: その他
 */
export type ReservationNumberPrefix = 'BD' | 'YE' | 'WB' | 'OT';

/**
 * 予約経路
 */
export type ReservationSource = 'HOT_PEPPER_BEAUTY' | 'PHONE' | 'WEBSITE' | 'OTHER' | 'WALK_IN';

/**
 * 詳細な予約ステータス
 */
export type DetailedReservationStatus = 
  | 'WAITING_RECEPTION'     // 受付待ち
  | 'RECEPTION_COMPLETED'   // 受付済み
  | 'IN_SERVICE'           // 施術中
  | 'COMPLETED'           // 済み
  | 'CUSTOMER_CANCELLED'  // お客様キャンセル
  | 'SALON_CANCELLED';    // サロンキャンセル

/**
 * 来店処理ステータス
 */
export type VisitProcessingStatus = 
  | 'PENDING'             // 未処理
  | 'AMOUNT_CONFIRMED'    // 金額確定済み
  | 'POINT_CONFIRMED';    // ポイント確定済み

/**
 * キャンセルポリシー種別
 */
export type CancellationPolicyType = 
  | 'SAME_DAY_50'        // 当日キャンセル50%
  | 'NO_SHOW_100'        // 無断キャンセル100%
  | 'CUSTOM';            // カスタム設定

/**
 * サロンボード互換予約情報
 */
export interface SalonBoardReservation {
  // 既存のReservation型を拡張
  id: string;
  
  // 予約番号（例: BD12345678）
  reservationNumber: string;
  
  // 予約経路
  source: ReservationSource;
  
  // 詳細ステータス
  detailedStatus: DetailedReservationStatus;
  
  // 金額情報
  amount: {
    subtotal: number;      // 小計
    tax: number;          // 消費税
    total: number;        // 合計
    discount: number;     // 割引額
    discountReason?: string; // 割引理由
  };
  
  // 来店処理
  visitProcessing: {
    status: VisitProcessingStatus;
    confirmedAt?: Date;     // 確定日時
    confirmedBy?: string;   // 確定者
    deadline: Date;         // 処理期限（施術日から6日以内）
  };
  
  // ポイント管理
  points: {
    earned: number;         // 獲得ポイント
    used: number;          // 使用ポイント
    earnedReason?: string; // 獲得理由
  };
  
  // キャンセルポリシー
  cancellationPolicy: {
    type: CancellationPolicyType;
    rate: number;          // パーセンテージ
    appliedAmount?: number; // 適用金額
  };
  
  // メモ機能（タイムスタンプ付き）
  reservationMemo?: {
    content: string;
    createdAt: Date;
    createdBy: string;
  };
  
  // 次回来店向けメモ
  nextVisitMemo?: {
    content: string;
    createdAt: Date;
    createdBy: string;
  };
}

/**
 * 顧客統合候補
 */
export interface CustomerMergeCandidate {
  customerId: string;
  similarity: number;      // 類似度（0-100）
  matchingFields: string[]; // 一致したフィールド
  suggestedAt: Date;
}

/**
 * メッセージ配信設定
 */
export interface MessageDeliverySettings {
  email: boolean;
  myPage: boolean;
  push?: boolean;
  sms?: boolean;
}

/**
 * 配信履歴
 */
export interface DeliveryHistory {
  id: string;
  type: 'EMAIL' | 'MYPAGE' | 'PUSH' | 'SMS';
  title: string;
  content: string;
  sentAt: Date;
  status: 'SENT' | 'FAILED' | 'PENDING';
  errorReason?: string;
}

export interface CustomerMergeCandidate {
  customerId: string;
  customerName: string;
  similarity: number;
  suggestedFields: string[];
  confidence: 'high' | 'medium' | 'low';
}

/**
 * サロンボード互換顧客情報
 */
export interface SalonBoardCustomer {
  // 既存のCustomer型を拡張
  id: number;
  
  // 顧客ID（例: C0000001）
  customerCode: string;
  
  // 統合管理
  mergeHistory: {
    mergedFrom?: string[];    // 統合元顧客ID
    mergedAt?: Date;
    mergedBy?: string;
  };
  
  // 統合候補
  mergeCandidates?: CustomerMergeCandidate[];
  
  // メッセージ配信設定
  messageSettings: MessageDeliverySettings;
  
  // 配信履歴
  deliveryHistory: DeliveryHistory[];
  
  // 来店回数（システム導入前）
  preSystemVisitCount?: number;
  
  // カスタムフィールド
  customFields: {
    other1?: string;
    other2?: string;
    other3?: string;
  };
}

/**
 * カルテ情報
 */
/**
 * 施術履歴エントリ
 */
export interface VisitHistory {
  id: string;
  date: string;
  services?: string[];
  staff?: string;
  duration?: number;
  reservationId?: string;
  treatmentNotes?: string;
  products?: string[];
  nextRecommendation?: string;
  amount?: number;
  status?: string;
}

export interface MedicalRecord {
  id: string;
  reservationId: string;
  customerId: number;
  visitHistoryId?: string;  // 施術履歴との紐付け用ID
  date: Date;
  
  // 施術内容詳細
  treatmentDetails: {
    menu: string;           // 施術メニュー
    staff: string;          // 担当スタッフ
    duration: number;       // 施術時間（分）
    techniques: string[];   // 使用技術
    products: string[];     // 使用商品
  };
  
  // 身体状態
  physicalCondition: {
    bloodPressure?: string;  // 血圧
    temperature?: number;    // 体温
    condition: string;       // 体調
    concerns: string[];      // 気になる箇所
  };
  
  // カウンセリング内容
  counseling: {
    requests: string;        // お客様の要望
    suggestions: string;     // 提案内容
    nextRecommendation: string; // 次回推奨
  };
  
  // 会話メモ
  conversationNotes: string;
  
  // その他メモ
  otherNotes: string;
  
  // 写真
  photos?: {
    id: string;
    url: string;
    type: 'BEFORE' | 'AFTER' | 'PROGRESS';
    takenAt: Date;
  }[];
  
  // タイムスタンプ
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy: string;
}

/**
 * ランダム数字生成ヘルパー関数
 */
function generateRandomNumber(digits: number): string {
  const min = Math.pow(10, digits - 1);
  const max = Math.pow(10, digits) - 1;
  return Math.floor(Math.random() * (max - min + 1) + min).toString();
}

/**
 * 予約番号生成ユーティリティ
 */
export function generateReservationNumber(
  prefix: ReservationNumberPrefix,
  _sequenceNumber: number
): string {
  // シーケンス番号の代わりに8桁のランダム数字を使用
  const randomNumber = generateRandomNumber(8);
  return `${prefix}${randomNumber}`;
}

/**
 * 顧客コード生成ユーティリティ
 */
export function generateCustomerCode(_sequenceNumber: number): string {
  // シーケンス番号の代わりに11桁のランダム数字を使用
  const randomNumber = generateRandomNumber(11);
  return `C${randomNumber}`;
}

/**
 * 来店処理期限計算ユーティリティ
 */
export function calculateVisitProcessingDeadline(visitDate: Date): Date {
  const deadline = new Date(visitDate);
  deadline.setDate(deadline.getDate() + 6);
  deadline.setHours(23, 59, 59, 999);
  return deadline;
}

/**
 * キャンセル料計算ユーティリティ
 */
export function calculateCancellationFee(
  amount: number,
  policyType: CancellationPolicyType,
  policyRate: number
): number {
  if (policyType === 'CUSTOM') {
    return Math.floor(amount * (policyRate / 100));
  }
  
  switch (policyType) {
    case 'SAME_DAY_50':
      return Math.floor(amount * 0.5);
    case 'NO_SHOW_100':
      return amount;
    default:
      return 0;
  }
}

/**
 * 統合型定義：既存の型を拡張
 */
export interface ExtendedReservation extends Omit<SalonBoardReservation, 'id'> {
  // Reservation型の必須フィールド
  id: string;
  customerId: number;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  staffId: string;
  staffName: string;
  serviceId: string;
  serviceName: string;
  date: Date;
  startTime: string;
  endTime: string;
  duration: number;
  status: 'confirmed' | 'pending' | 'cancelled' | 'completed';
  notes: string;
  color?: string;
  createdAt?: Date;
  updatedAt?: Date;
  // 追加プロパティ（Reservationとの互換性）
  isVip?: boolean;
  price?: number;
  customerTier?: 'regular' | 'gold' | 'platinum' | 'diamond';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  changeHistory?: any[];
}

export interface ExtendedCustomer extends Omit<SalonBoardCustomer, 'id'> {
  // Customer型の必須フィールド
  id: number;
  name: string;
  nameKana?: string;
  email: string;
  phone: string;
  birthday?: string;
  occupation?: string;
  address?: string;
  allergies?: string;
  skinType?: string;          // 旧形式（後方互換性のため残す）
  skinTypes?: string[];       // 新形式（複数選択）
  hairTypes?: string[];       // 新形式（複数選択）
  preferences?: string;
  lastVisit: string;
  totalVisits: number;
  totalSpent: number;
  status: 'active' | 'inactive';
  rank: 'bronze' | 'silver' | 'gold' | 'platinum';
  tags: string[];
  preferredStaff?: string;
  visitHistory?: VisitHistory[];
  medicalInfo?: Record<string, unknown>;
  medicalRecords?: MedicalRecord[];  // カルテ情報配列
  createdAt: string;
  updatedAt: string;
  
  // Phase 2追加フィールド
  birthDate?: string;        // 誕生日（birthdayと統合予定）
  gender?: 'male' | 'female' | 'other' | 'unspecified';
  firstVisitDate?: string;   // 初回来店日
  bookingSource?: string;    // 予約経路
  memo?: string;            // 顧客メモ
  phoneSecondary?: string;  // 電話番号2
  isVip?: boolean;          // VIPステータス
}